import type { RelativePerformancePanelConfig } from '@src/types/LayoutConfig';
import {
  Box,
  createDateOnlyFromDate,
  FormControlSizes,
  getColorScale,
  getDateRangeFromLookback,
  HStack,
  Toggle,
  VStack,
  type DateOnly,
  type DateRangeOrLookback,
} from '@talos/kyoko';
import type { SeriesLineOptions } from 'highcharts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useAvailableMetrics } from '../AggregatedMetric/useAvailableMetrics';
import {
  formatRelativePercent,
  resolveMetricDisplayName,
  resolveSignalsValueFormatter,
  sameStringList,
  SignalsLineChart,
  SignalsModuleShell,
  SignalsTimeRangeControl,
  useMetricDisplayNameMap,
  useSignalsAssetMap,
  type SignalsTimeRange,
} from '../core';
import { ComparisonLegend } from './ComparisonLegend';
import { ComparisonTable } from './ComparisonTable';
import { ComparisonToolbar } from './ComparisonToolbar';
import { computeDateRangeForTimeRange } from './dateRange';
import {
  RELATIVE_PERFORMANCE_DEFAULT_CM_ASSET_IDS,
  RELATIVE_PERFORMANCE_DEFAULT_METRIC,
  RELATIVE_PERFORMANCE_DEFAULT_TIME_RANGE,
  RELATIVE_PERFORMANCE_MAX_ASSETS,
} from './tokens';
import { useComparisonData } from './useComparisonData';

interface ComparisonModuleProps {
  params: RelativePerformancePanelConfig;
  onParamsChange: (partial: Partial<RelativePerformancePanelConfig>) => void;
}

export function ComparisonModule({ params, onParamsChange }: ComparisonModuleProps) {
  const theme = useTheme();
  const cmAssetIds = params.cmAssetIds ?? RELATIVE_PERFORMANCE_DEFAULT_CM_ASSET_IDS;
  const metric = params.metric ?? RELATIVE_PERFORMANCE_DEFAULT_METRIC;
  // undefined = never set → use default preset; null = user picked a custom range → no active preset
  const activeTimeRange =
    params.activeTimeRange !== undefined ? params.activeTimeRange : RELATIVE_PERFORMANCE_DEFAULT_TIME_RANGE;
  const isRelative = params.isRelative ?? true;

  // Inline fallback avoids an init effect that would write params on every new panel mount.
  const defaultDates = useMemo(
    () => computeDateRangeForTimeRange(params.activeTimeRange ?? RELATIVE_PERFORMANCE_DEFAULT_TIME_RANGE),
    [params.activeTimeRange]
  );
  const startDate = (params.startDate ?? defaultDates.startDate) as DateOnly;
  const endDate = (params.endDate ?? defaultDates.endDate) as DateOnly;

  // Local picker state so shortcut highlights update immediately, not after the parent params round-trip.
  const [datePickerValue, setDatePickerValue] = useState<DateRangeOrLookback>(() => ({
    from: new Date(startDate + 'T00:00:00'),
    to: new Date(endDate + 'T00:00:00'),
  }));

  const metricsForAssetMap = useMemo(() => [metric], [metric]);
  const {
    talosSymbols: availableTalosSymbols,
    cmAssetIdByTalosSymbol,
    talosSymbolByCmAssetId,
    isLoading: isAssetsLoading,
    isError: isAssetsError,
  } = useSignalsAssetMap(metricsForAssetMap);

  useEffect(() => {
    if (isAssetsLoading || isAssetsError) {
      return;
    }
    const current = params.cmAssetIds ?? RELATIVE_PERFORMANCE_DEFAULT_CM_ASSET_IDS;
    const filtered = current.filter(id => talosSymbolByCmAssetId.has(id));
    if (filtered.length !== current.length) {
      onParamsChange({ cmAssetIds: filtered });
    }
  }, [isAssetsLoading, isAssetsError, talosSymbolByCmAssetId, params.cmAssetIds, onParamsChange]);

  // Pre-warm the catalog query so the metric picker loads before the user opens the settings.
  const {
    metrics: availableMetrics,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
  } = useAvailableMetrics(cmAssetIds);

  const { displayNameByMetricId } = useMetricDisplayNameMap();
  const getMetricLabel = useCallback(
    (metricId: string) => resolveMetricDisplayName(metricId, displayNameByMetricId),
    [displayNameByMetricId]
  );

  const { normalizedSeries, absoluteSeries, tableRows, isFetching, isError, isTruncated } = useComparisonData({
    cmAssetIds,
    metric,
    startDate,
    endDate,
    talosSymbolByCmAssetId,
  });

  const selectedTalosSymbols = useMemo(() => {
    const symbols: string[] = [];
    for (const id of cmAssetIds) {
      const symbol = talosSymbolByCmAssetId.get(id);
      if (symbol != null) {
        symbols.push(symbol);
      }
    }
    return symbols;
  }, [cmAssetIds, talosSymbolByCmAssetId]);

  const handleSelectedTalosSymbolsChange = useCallback(
    (talosSymbols: string[]) => {
      const limited = talosSymbols.slice(0, RELATIVE_PERFORMANCE_MAX_ASSETS);
      const ids: string[] = [];
      for (const sym of limited) {
        const id = cmAssetIdByTalosSymbol.get(sym);
        if (id != null) {
          ids.push(id);
        }
      }
      if (!sameStringList(ids, cmAssetIds)) {
        onParamsChange({ cmAssetIds: ids });
      }
    },
    [cmAssetIdByTalosSymbol, cmAssetIds, onParamsChange]
  );

  const handleMetricChange = useCallback(
    (next: string) => {
      if (next !== metric) {
        onParamsChange({ metric: next });
      }
    },
    [metric, onParamsChange]
  );

  const handleTimeRangeChange = useCallback(
    (range: SignalsTimeRange) => {
      const dates = computeDateRangeForTimeRange(range);
      // Update the picker value immediately so the DateRange is shown without waiting for params.
      setDatePickerValue({
        from: new Date(dates.startDate + 'T00:00:00'),
        to: new Date(dates.endDate + 'T00:00:00'),
      });
      onParamsChange({ activeTimeRange: range, startDate: dates.startDate, endDate: dates.endDate });
    },
    [onParamsChange]
  );

  const handleDateRangeChange = useCallback(
    (value: DateRangeOrLookback) => {
      // Update local picker state immediately — this is what keeps shortcuts highlighted.
      setDatePickerValue(value);
      const { from, to: rawTo } = getDateRangeFromLookback(value);
      // Lookback shortcuts return to: null (meaning "up to now"), so fall back to today.
      const to = rawTo ?? new Date();
      if (!from) {
        return;
      }
      onParamsChange({
        startDate: createDateOnlyFromDate(from),
        endDate: createDateOnlyFromDate(to),
        activeTimeRange: null,
      });
    },
    [onParamsChange]
  );

  const handleIsRelativeChange = useCallback(
    (next: boolean) => {
      if (next !== isRelative) {
        onParamsChange({ isRelative: next });
      }
    },
    [isRelative, onParamsChange]
  );

  const activeSeries = isRelative ? normalizedSeries : absoluteSeries;

  const chartColors = useMemo(() => getColorScale(theme, 12), [theme]);

  const chartSeries = useMemo<SeriesLineOptions[]>(
    () => activeSeries.map((s, i) => ({ type: 'line', name: s.talosSymbol, data: s.data, color: chartColors[i] })),
    [activeSeries, chartColors]
  );

  const formatValue = isRelative ? formatRelativePercent : resolveSignalsValueFormatter(metric);
  const formatMetricValue = useMemo(() => resolveSignalsValueFormatter(metric), [metric]);

  const rowColors = useMemo(
    () => new Map(activeSeries.map((s, i) => [s.cmAssetId, chartColors[i]])),
    [activeSeries, chartColors]
  );

  const legendItems = useMemo(() => {
    const tableRowByCmAssetId = new Map(tableRows.map(row => [row.cmAssetId, row]));
    return activeSeries.flatMap(s => {
      const row = tableRowByCmAssetId.get(s.cmAssetId);
      if (row == null) {
        return [];
      }
      return [
        {
          symbol: row.talosSymbol,
          value: row.changePercent != null ? formatRelativePercent(row.changePercent) : '—',
          color: rowColors.get(s.cmAssetId) ?? chartColors[0],
          isPositive: row.changePercent != null ? row.changePercent >= 0 : undefined,
        },
      ];
    });
  }, [activeSeries, tableRows, rowColors, chartColors]);

  const isLoading = isFetching && chartSeries.length === 0;
  const isErrorOrTruncated = isError || isTruncated;
  const errorMessage = isTruncated
    ? 'Too many assets for the selected range. Remove some assets or choose a shorter time range.'
    : 'Failed to load Relative Performance.';

  return (
    <SignalsModuleShell
      header={
        <ComparisonToolbar
          metric={metric}
          availableMetrics={availableMetrics}
          isMetricsLoading={isMetricsLoading}
          isMetricsError={isMetricsError}
          onMetricChange={handleMetricChange}
          getMetricLabel={getMetricLabel}
          selectedTalosSymbols={selectedTalosSymbols}
          availableTalosSymbols={availableTalosSymbols}
          onSelectedTalosSymbolsChange={handleSelectedTalosSymbolsChange}
          maxSelectedAssets={RELATIVE_PERFORMANCE_MAX_ASSETS}
          dateRange={datePickerValue}
          onDateRangeChange={handleDateRangeChange}
        />
      }
      isLoading={isLoading}
      isError={isErrorOrTruncated}
      errorMessage={errorMessage}
      controls={
        <HStack gap="spacing8" flex="0 0 auto">
          <Toggle
            label="Relative"
            checked={isRelative}
            onChange={handleIsRelativeChange}
            size={FormControlSizes.Small}
            data-testid="comparison-relative-toggle"
          />
          <SignalsTimeRangeControl
            value={activeTimeRange ?? undefined}
            onChange={handleTimeRangeChange}
            disabled={isFetching || isErrorOrTruncated}
          />
        </HStack>
      }
    >
      <VStack flex="1" w="100%" h="100%" gap="0" overflow="hidden">
        <Box position="relative" flex="1" w="100%" minHeight="0" overflow="hidden">
          <ComparisonLegend items={legendItems} />
          <SignalsLineChart key={isRelative ? 'relative' : 'absolute'} series={chartSeries} formatValue={formatValue} />
        </Box>
        <Box w="100%" flex="0 0 40%" minHeight="0" overflow="hidden">
          <ComparisonTable
            rows={tableRows}
            formatMetricValue={formatMetricValue}
            rowColors={rowColors}
            metricDisplayName={getMetricLabel(metric)}
          />
        </Box>
      </VStack>
    </SignalsModuleShell>
  );
}
